// Build-time content hash pin for the hosted chat widget (task 8.5) — see
// scripts/pin-chat-widget.mts.
import { CHAT_WIDGET_PIN } from "./generated/chat-widget-pin";

type EnvBag = Partial<Record<string, string | undefined>>;

// An ABSENT `import.meta.env` means the config graph (vite.config's import
// chain, where prerender/sitemap URLs are computed) — that context must
// resolve as production, never fall through to dev-default localhost URLs.
const env: EnvBag = (import.meta as { env?: EnvBag }).env ?? {
  MODE: "production",
};

// Vite inlines `import.meta.env.MODE`; `process.env.NODE_ENV` is not defined
// in the browser bundle.
const isProd = env.MODE === "production";

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, "");

/**
 * Canonical hosted-widget URLs. The socket origin is widget.furistic.co in
 * production (RealtyOS landing pattern); locally the widget socket resolves
 * from the backendUrl origin (DEFAULT_URLS.FUR_GATEWAY, port 42600).
 */
const CANONICAL_CHAT_SOCKET_URL = "https://widget.furistic.co";

const gatewayRoot = trimTrailingSlash(
  env.VITE_API_GATEWAY_URL ||
    (isProd ? "https://api.furistic.co" : "http://localhost:42600"),
);
const chatSocketRoot = trimTrailingSlash(
  env.VITE_CHAT_WIDGET_SOCKET_URL ||
    (isProd ? CANONICAL_CHAT_SOCKET_URL : "http://localhost:42550"),
);

export const URLS = {
  SITE:
    env.VITE_SITE_URL ||
    (isProd ? "https://furistic.co" : "http://localhost:44200"),
  /** API gateway base URL handed to the chat widget as its backend. */
  CHAT_BACKEND: gatewayRoot,
  /** Socket.IO origin the chat widget connects to. */
  SOCKET_IO: chatSocketRoot,
  /**
   * Hosted widget script. Defaults to the build-time-resolved, content-hashed
   * pin (supply-chain: the browser refuses any script whose bytes do not match
   * the recorded SRI digest); an explicit env override wins for local dev.
   */
  WIDGET_SCRIPT: env.VITE_CHAT_WIDGET_SCRIPT_URL || CHAT_WIDGET_PIN.scriptUrl,
};

/**
 * The resolved SRI digest for WIDGET_SCRIPT's default (undefined when a local
 * dev override is in play — digests only pin the canonical hosted bundle).
 */
export const CHAT_WIDGET_SCRIPT_INTEGRITY =
  !env.VITE_CHAT_WIDGET_SCRIPT_URL ||
  env.VITE_CHAT_WIDGET_SCRIPT_URL === CHAT_WIDGET_PIN.scriptUrl
    ? CHAT_WIDGET_PIN.integrity
    : undefined;
